import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth as useSupabaseAuth } from '../../contexts/AuthContext';
import { useAuth as useAppAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import {
  CURRENT_TERMS_VERSION,
  CURRENT_PRIVACY_VERSION,
  RECONSENT_GATE_MODE,
  consentOutdated,
} from '../../config/consent';

/**
 * Blocking re-consent gate.
 *
 * When the required Terms/Privacy version (config/consent.ts) is newer than what
 * the signed-in user accepted (stored in Supabase user_metadata), this overlays
 * a modal that must be accepted before continuing.
 *
 * Rollout is controlled by RECONSENT_GATE_MODE:
 *  - 'off'   : never shows (feature can be tested live without nagging users)
 *  - 'admin' : only admins are gated (smoke test)
 *  - 'all'   : everyone with outdated consent is gated (full launch)
 *
 * New signups already write the current versions in AuthCallback, so they never
 * see it.
 */

// Auth/legal routes where the gate must NOT appear (so users can sign in and
// actually read the documents they are being asked to accept).
const EXEMPT_PREFIXES = [
  '/auth',
  '/login',
  '/signup',
  '/recruiter-signup',
  '/signup-choice',
  '/reset-password',
  '/forgot-password',
  '/email-confirmation',
  '/resend-confirmation',
  '/terms',
  '/privacy',
  '/imprint',
  '/cookies',
  '/legal',
];

const ConsentGate: React.FC = () => {
  const { user, loading } = useSupabaseAuth();
  const { apiCall } = useAppAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null = unknown yet; only resolved when we need it ('admin' mode)
  const [isAdmin, setIsAdmin] = useState<boolean | null>(RECONSENT_GATE_MODE === 'admin' ? null : true);

  const de = (i18n.language || '').toLowerCase().startsWith('de');
  const exempt = EXEMPT_PREFIXES.some((p) => location.pathname.startsWith(p));
  const outdated = !!user && consentOutdated(user.user_metadata);

  // In 'admin' mode we must know whether this user is an admin before gating.
  useEffect(() => {
    let cancelled = false;
    if (RECONSENT_GATE_MODE !== 'admin') return;
    if (loading || !user || exempt || !outdated) return;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const res = await apiCall(`${apiBase}/api/v1/profiles/${user.id}`);
        if (cancelled) return;
        if (res.ok) {
          const p = await res.json();
          setIsAdmin(p.is_admin === true);
        } else {
          setIsAdmin(false);
        }
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.id, exempt, outdated]);

  if (RECONSENT_GATE_MODE === 'off') return null;
  if (loading || !user) return null;
  if (exempt) return null;
  if (!outdated) return null;
  if (RECONSENT_GATE_MODE === 'admin' && isAdmin !== true) return null;

  const handleAccept = async () => {
    if (!accepted || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          terms_accepted_at: now,
          terms_version: CURRENT_TERMS_VERSION,
          privacy_accepted_at: now,
          privacy_version: CURRENT_PRIVACY_VERSION,
        },
      });
      if (updateError) throw updateError;
      // The AuthContext listens to USER_UPDATED and will re-render → gate hides.
    } catch (e: any) {
      setError(de ? 'Konnte nicht gespeichert werden. Bitte erneut versuchen.' : 'Could not save. Please try again.');
      setSubmitting(false);
    }
  };

  const t = {
    title: de ? 'Aktualisierte Bedingungen' : 'Updated terms',
    body: de
      ? 'Wir haben unsere AGB und Datenschutzerklärung aktualisiert. Bitte bestätige, um Cevee weiter zu nutzen.'
      : 'We have updated our Terms and Privacy Policy. Please confirm to keep using Cevee.',
    checkbox: de ? 'Ich akzeptiere die' : 'I accept the',
    terms: de ? 'AGB' : 'Terms',
    and: de ? 'und die' : 'and the',
    privacy: de ? 'Datenschutzerklärung' : 'Privacy Policy',
    accept: de ? 'Akzeptieren & fortfahren' : 'Accept & continue',
    logout: de ? 'Abmelden' : 'Log out',
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-2">{t.title}</h2>
        <p className="text-sm text-slate-600 mb-4 leading-relaxed">{t.body}</p>

        <label className="flex items-start gap-2 text-sm text-slate-700 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
          />
          <span>
            {t.checkbox}{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">{t.terms}</Link>{' '}
            {t.and}{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">{t.privacy}</Link>.
          </span>
        </label>

        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

        <button
          onClick={handleAccept}
          disabled={!accepted || submitting}
          className="w-full py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 transition-all"
        >
          {submitting ? '…' : t.accept}
        </button>

        <button
          onClick={() => supabase.auth.signOut()}
          className="w-full mt-2 py-2 text-xs text-slate-400 hover:text-slate-600"
        >
          {t.logout}
        </button>
      </div>
    </div>
  );
};

export default ConsentGate;
